import { useEffect, useState } from "react"
import { useFormik } from "formik"
import { useQueryState } from "nuqs"
import Loader from "../../components/Loader"
import PatientOrdersStatistics from "./components/PatientOrdersStatistics"
import OrderResultDetails from "./components/OrderResultDetails"
import useFetchPatientIssuers from "../../hooks/useFetchPatientIssuers"
import DashboardStatisticsCards from "./components/DashboardStatisticsCards"
import { DateTime } from "luxon"
import { dbDateFormat } from "../../constants"
import DatePicker from "../../components/DatePicker"
import { orderStatisticsFilterSchema } from "./schema"
import { useSearchParams } from "react-router-dom"

const ISSUER_LABELS = { SCANPROD: "SCAN" }
const issuerLabel = (value) => ISSUER_LABELS[value] || value

export const OrderComponents = () => {
  const { data, isLoading } = useFetchPatientIssuers()
  // Define sorting states for each component
  const [orderStatsSorting] = useState([{ id: "PatientIssuer", desc: false }])
  const [resultStatsSorting, setResultStatsSorting] = useState([
    { id: "PatientIssuer", desc: false },
  ])
  const [searchParams, setSearchParams] = useSearchParams()
  const [patientIssuerParam, setPatientIssuerParam] = useQueryState(
    "patientIssuer",
    { defaultValue: "" }
  )

  const fromDate = searchParams.get("from") ?? null
  const toDate = searchParams.get("to") ?? null

  useEffect(() => {
    if (!fromDate || !toDate) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev)
        newParams.set("from", DateTime.now().toFormat(dbDateFormat))
        newParams.set("to", DateTime.now().toFormat(dbDateFormat))
        return newParams
      })
    }
  }, [fromDate, setSearchParams, toDate])

  // Central formik to manage all filters
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      from: fromDate || DateTime.now().toFormat(dbDateFormat),
      to: toDate || DateTime.now().toFormat(dbDateFormat),
      patientIssuer: patientIssuerParam,
      status: "",
    },
    validationSchema: orderStatisticsFilterSchema,
    onSubmit: () => {},
    validateOnChange: true,
  })

  const { values, setFieldValue, errors } = formik

  if (isLoading) {
    return (
      <div className="py-12 flex justify-center items-center gap-3">
        <Loader className="border-primary" />
        <p>Loading...</p>
      </div>
    )
  }

  const allIssuers = Array.from(new Set(data?.clients || [])).sort((a, b) =>
    String(a).localeCompare(String(b))
  )

  return (
    <div className="mt-2 py-2 px-4 flex flex-col gap-3">
      <div className="bg-primary p-6 rounded-lg shadow-md">
        <div className="flex flex-col lg:flex-row justify-between lg:items-end gap-4">
          <h1 className="text-2xl text-white font-bold">
            Statistics Dashboard
          </h1>

          <div className="flex items-end gap-4 flex-wrap">
            <div className="flex flex-col">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-white/80 mb-1">
                Patient Issuer
              </label>
              <select
                className="bg-white border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-secondary/30 cursor-pointer min-w-[190px]"
                value={values.patientIssuer}
                onChange={(e) => {
                  setFieldValue("patientIssuer", e.target.value)
                  setPatientIssuerParam(e.target.value || null)
                }}
              >
                <option value="">All Issuers</option>
                {allIssuers.map((item, index) => (
                  <option key={index} value={item}>
                    {issuerLabel(item)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-white/80 mb-1">
                From Date
              </label>
              <DatePicker
                required
                endDate={values.to}
                error={errors.from}
                maxDate={values.to || new Date()}
                value={values.from}
                onChange={(date) => {
                  const fromDate = DateTime.fromJSDate(date, {
                    zone: "local",
                  }).toFormat(dbDateFormat)
                  setFieldValue("from", fromDate)
                  setSearchParams((prev) => {
                    const newParams = new URLSearchParams(prev)
                    newParams.set("from", fromDate)
                    return newParams
                  })
                  if (!values.to || date > values.to) {
                    setFieldValue("to", null)
                  }
                }}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-white/80 mb-1">
                To Date
              </label>
              <DatePicker
                required
                error={errors.to}
                maxDate={new Date()}
                minDate={values.from}
                value={values.to}
                onChange={(date) => {
                  const toDate = DateTime.fromJSDate(date, {
                    zone: "local",
                  }).toFormat(dbDateFormat)
                  setFieldValue("to", toDate)
                  setSearchParams((prev) => {
                    const newParams = new URLSearchParams(prev)
                    newParams.set("to", toDate)
                    return newParams
                  })
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Statistics Cards */}
      <DashboardStatisticsCards filters={values} />

      <div className="flex flex-wrap gap-6 w-full">
        <div className="w-full">
          <PatientOrdersStatistics
            clients={data?.clients || []}
            filters={values}
            sorting={orderStatsSorting}
          />
        </div>
        <div className="w-full">
          <OrderResultDetails
            clients={data?.clients || []}
            filters={values}
            sorting={resultStatsSorting}
            variant="clients"
            onSortingChange={setResultStatsSorting}
          />
        </div>
      </div>
    </div>
  )
}

export default OrderComponents
